import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowUpRight, Star, GitFork, Github } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';
import { useLiveStatus } from '../hooks/useLiveStatus';
import { useDemoClips } from '../hooks/useDemoClips';
import { featuredProjects, visibleProjects } from '../data/projects';
import { caseStudyIndex } from '../data/caseStudyIndex';

// Dot colors for the most common languages; everything else falls back to muted.
const LANGUAGE_COLORS = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  Python: '#3572A5',
  Go: '#00ADD8',
  Rust: '#dea584',
  Solidity: '#AA6746',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Shell: '#89e051',
  Java: '#b07219',
  'C++': '#f34b7d',
  Ruby: '#701516',
};

// Every project now ships a self-hosted capture from
// scripts/capture-screenshots.js. The old fallback hit a third-party live
// screenshot service on every card render, which was slow, sometimes returned
// a grey placeholder, and put the look of the page in someone else's hands.
// Re-run the script when a site gets a redesign.

// ============ SHARED PIECES ============

export const SectionHeading = ({ kicker, children, isInView, className = '' }) => (
  <div className={`mb-12 ${className}`}>
    {kicker && (
      <p
        className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${
          isInView ? 'animate-fade-in-up' : 'opacity-0'
        }`}
      >
        {kicker}
      </p>
    )}
    <h2
      className={`text-3xl md:text-4xl text-[#051A24] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: '0.1s' }}
    >
      {children}
    </h2>
  </div>
);

// The badge reports the last check, not the authored word. `checked` is
// 'live' | 'offline' | null, where null means nobody has checked recently and
// the project's own `status` stands in.
const StatusBadge = ({ status, checked, checkedAt }) => {
  const label = checked === 'live' ? 'Live' : checked === 'offline' ? 'Offline' : status;
  if (!label) return null;

  const isLive = label.toLowerCase() === 'live';
  const isOffline = label.toLowerCase() === 'offline';
  const title = checkedAt
    ? `Checked ${new Date(checkedAt).toLocaleString()}`
    : 'Not checked recently';

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
        isOffline
          ? 'bg-[#E0EBF0] text-[#273C46]'
          : isLive
            ? 'bg-[#051A24] text-[#F6FCFF]'
            : 'bg-[#E0EBF0] text-[#273C46]'
      }`}
      data-testid={`status-badge-${label.toLowerCase()}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isOffline ? 'bg-amber-500' : isLive ? 'bg-emerald-400' : 'bg-[#273C46]/50'
        }`}
      />
      {label}
    </span>
  );
};

const ProductMedia = ({ product, checked, checkedAt, clip }) => {
  // Order of preference: a recorded demo loop, then the screenshot, then a
  // styled title card. A clip only reaches this component if the manifest says
  // the file exists, and if it fails to decode anyway the poster is already the
  // screenshot underneath it.
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const frameRef = useRef(null);

  const src = product.image;
  const showImage = Boolean(src) && !failed;
  const isContain = product.imageFit === 'contain';
  const showClip = Boolean(clip) && !failed;

  // Play only while the tile is on screen, and never for a visitor who asked for
  // reduced motion — a looping video is exactly what that setting is about.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !showClip) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting;
        const video = videoRef.current;
        if (!video) return;
        if (visible) {
          video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
          video.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [showClip]);

  return (
    <div ref={frameRef} className="aspect-[16/10] overflow-hidden bg-[#E0EBF0] relative">
      {showClip ? (
        <video
          ref={videoRef}
          src={clip.src}
          poster={clip.poster || src}
          muted
          loop
          playsInline
          preload="none"
          aria-label={`${product.title} — recorded demo`}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : showImage ? (
        <img
          src={src}
          alt={`${product.title} — product screenshot`}
          loading="lazy"
          className={`w-full h-full ${
            isContain ? 'object-contain p-3' : 'object-cover'
          } transition-transform duration-500 group-hover:scale-105`}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#051A24] to-[#273C46] px-6 text-center">
          <span
            className="text-2xl text-[#F6FCFF]/90 italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {product.title}
          </span>
        </div>
      )}

      <div className="absolute top-4 left-4">
        <StatusBadge status={product.status} checked={checked} checkedAt={checkedAt} />
      </div>

      {/* Say what it is. A moving tile that does not explain itself reads as an
          ad; "recorded" also says nobody's live service is being hit. */}
      {(showClip || product.mediaNote) && (
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-[#051A24]/80 text-[#F6FCFF] backdrop-blur-sm">
          {showClip && (
            <span className={`w-1.5 h-1.5 rounded-full ${playing ? 'bg-emerald-400' : 'bg-[#E0EBF0]/60'}`} />
          )}
          {[showClip && 'Recorded demo', product.mediaNote].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  );
};

// One card shape everywhere. The live product is the card's primary link,
// stretched over the whole card; the case study sits in a footer that every
// card has, so the bottom edges line up across a row whether or not a project
// has a write-up. Unshipped work gets no link at all rather than an anchor to
// '#', which scrolls to the top and reads as broken.
const FOOTER_BUTTON =
  'relative z-10 inline-flex items-center gap-1.5 text-sm font-medium min-h-[44px] transition-all hover:gap-2.5';

export const ProductCard = ({
  product,
  index = 0,
  isInView,
  checked = null,
  checkedAt = null,
  clip = null,
  onNavigateCaseStudy,
}) => {
  const caseStudy = product.caseStudy && onNavigateCaseStudy;

  return (
    <article
      className={`group relative flex flex-col h-full w-full bg-white border border-[#E0EBF0] rounded-2xl overflow-hidden transition-all duration-300 ${
        product.link ? 'hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-[#273C46]/30' : ''
      } ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: `${0.15 + index * 0.08}s` }}
      data-testid={`product-card-${product.slug || index}`}
    >
      <ProductMedia product={product} checked={checked} checkedAt={checkedAt} clip={clip} />

      <div className="p-6 pb-0 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-[#051A24] mb-2">
          {product.link ? (
            <a
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              className="after:absolute after:inset-0 after:content-[''] group-hover:text-[#273C46] transition-colors focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-[#051A24] focus-visible:after:rounded-2xl"
            >
              {product.title}
            </a>
          ) : (
            product.title
          )}
        </h3>
        <p className="text-sm text-[#273C46] leading-relaxed mb-4 line-clamp-3 min-h-[4.5rem]" title={product.summary}>
          {product.summary}
        </p>
        {product.tags?.length > 0 && (
          // One row of tags, always, so every card's footer starts at the same
          // height. Two tags fit the narrowest (4-up) card.
          <div className="flex flex-nowrap gap-2 mt-auto overflow-hidden">
            {product.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="whitespace-nowrap text-xs font-medium px-2.5 py-1 rounded-full bg-[#E0EBF0] text-[#273C46]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mx-6 mt-5 mb-1 border-t border-[#E0EBF0] flex items-center justify-between gap-3 min-h-[52px]">
        {product.link ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#051A24]" aria-hidden="true">
            Visit <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        ) : (
          <span className="text-sm text-[#273C46]/70">Not public yet</span>
        )}
        <div className="flex items-center gap-4">
          {product.sandbox && (
            <a
              href={product.sandbox.href}
              target="_blank"
              rel="noopener noreferrer"
              title={product.sandbox.note}
              className={`${FOOTER_BUTTON} text-[#273C46] hover:text-[#051A24]`}
              data-testid={`product-sandbox-${product.slug}`}
            >
              {product.sandbox.label || 'Try it'}
            </a>
          )}
          {caseStudy && (
            <button
              type="button"
              onClick={() => onNavigateCaseStudy(product.caseStudy)}
              className={`${FOOTER_BUTTON} text-[#051A24]`}
              data-testid={`product-case-study-${product.caseStudy}`}
            >
              Case study <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

// Column count follows the item count, so a row never ends with one stranded
// card: four sit 4-up (2-up on tablets), everything else 3-up. Literal class
// strings so Tailwind keeps them.
const gridColumns = (count) => {
  if (count === 4) return 'md:grid-cols-2 xl:grid-cols-4';
  if (count === 2) return 'md:grid-cols-2';
  if (count === 1) return '';
  return 'md:grid-cols-2 lg:grid-cols-3';
};

export const ProjectGrid = ({ items, isInView, onNavigateCaseStudy }) => {
  const { statusFor, checkedAtFor } = useLiveStatus();
  const { clipFor } = useDemoClips();

  return (
    <div className={`grid grid-cols-1 ${gridColumns(items.length)} gap-6 items-stretch`}>
      {items.map((item, index) => (
        <ProductCard
          key={item.slug || item.title}
          product={item}
          index={index}
          isInView={isInView}
          checked={statusFor(item.link)}
          checkedAt={checkedAtFor(item.link)}
          clip={clipFor(item.slug)}
          onNavigateCaseStudy={onNavigateCaseStudy}
        />
      ))}
    </div>
  );
};

// ============ HOMEPAGE: SELECTED WORK ============
// Four projects, one grid, one reading behaviour. The full list lives on /work.

export const SelectedWorkSection = ({ onNavigateWork, onNavigateCaseStudy, onNavigateCaseStudies }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      id="work"
      className="max-w-[1200px] mx-auto px-6 py-20 scroll-mt-24"
      data-testid="selected-work-section"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p
            className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            Selected work
          </p>
          <h2
            className={`text-3xl md:text-4xl text-[#051A24] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.1s' }}
          >
            Four things I{' '}
            <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
              shipped
            </span>
          </h2>
        </div>
        <button
          type="button"
          onClick={onNavigateWork}
          className={`inline-flex items-center gap-2 text-sm font-medium text-[#051A24] hover:gap-3 transition-all self-start md:self-auto py-3 min-h-[44px] ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
          style={{ animationDelay: '0.2s' }}
          data-testid="selected-work-view-all"
        >
          See all {visibleProjects.length} projects <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      <ProjectGrid
        items={featuredProjects}
        isInView={isInView}
        onNavigateCaseStudy={onNavigateCaseStudy}
      />

      {/* One line under the grid for anyone who wants the engineering rather
          than the pitch. The running products are still the argument. */}
      <div
        className={`mt-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#273C46] ${
          isInView ? 'animate-fade-in-up' : 'opacity-0'
        }`}
        style={{ animationDelay: '0.5s' }}
        data-testid="selected-work-case-studies"
      >
        <span className="text-[#273C46]/70">How they were built:</span>
        <button
          type="button"
          onClick={onNavigateCaseStudies}
          className="inline-flex items-center gap-1.5 font-medium text-[#051A24] hover:gap-2.5 transition-all min-h-[44px]"
          data-testid="home-case-studies-link"
        >
          {caseStudyIndex.length} case studies <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};

// ---- Open Source (live GitHub feed) ----

const RepoCard = ({ repo, index, isInView }) => {
  const langColor = LANGUAGE_COLORS[repo.language] || '#273C46';
  return (
    <a
      href={repo.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex flex-col bg-white border border-[#E0EBF0] rounded-2xl p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-[#273C46]/30 transition-all duration-300 ${
        isInView ? 'animate-fade-in-up' : 'opacity-0'
      }`}
      style={{ animationDelay: `${0.2 + (index % 6) * 0.08}s` }}
      data-testid={`repo-card-${repo.name}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Github className="w-4 h-4 text-[#273C46] flex-shrink-0" />
          <h3 className="font-semibold text-[#051A24] truncate group-hover:text-[#273C46] transition-colors">
            {repo.name}
          </h3>
        </div>
        <ArrowUpRight className="w-5 h-5 text-[#273C46]/60 group-hover:text-[#051A24] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      </div>

      <p className="text-sm text-[#273C46] leading-relaxed mb-5 flex-1 line-clamp-3">
        {repo.description || 'No description provided.'}
      </p>

      {repo.topics?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {repo.topics.slice(0, 3).map((topic) => (
            <span
              key={topic}
              className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#E0EBF0] text-[#273C46]"
            >
              {topic}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 mt-auto pt-4 border-t border-[#E0EBF0]/70 text-xs text-[#273C46]">
        {repo.language && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: langColor }} />
            {repo.language}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Star className="w-3.5 h-3.5" /> {repo.stars}
        </span>
        <span className="flex items-center gap-1">
          <GitFork className="w-3.5 h-3.5" /> {repo.forks}
        </span>
      </div>
    </a>
  );
};

const RepoSkeleton = () => (
  <div className="bg-white border border-[#E0EBF0] rounded-2xl p-6 h-[220px]">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-4 h-4 rounded-full bg-[#E0EBF0] animate-pulse" />
      <div className="h-4 w-1/2 rounded bg-[#E0EBF0] animate-pulse" />
    </div>
    <div className="space-y-2 mb-6">
      <div className="h-3 w-full rounded bg-[#E0EBF0]/70 animate-pulse" />
      <div className="h-3 w-5/6 rounded bg-[#E0EBF0]/70 animate-pulse" />
    </div>
    <div className="flex gap-4">
      <div className="h-3 w-16 rounded bg-[#E0EBF0]/70 animate-pulse" />
      <div className="h-3 w-10 rounded bg-[#E0EBF0]/70 animate-pulse" />
    </div>
  </div>
);

export const OpenSourceSection = () => {
  const { ref, isInView } = useInViewAnimation();
  const [repos, setRepos] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!isInView || hasFetched.current) return;
    hasFetched.current = true;

    fetch('/api/github')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Request failed'))))
      .then((data) => {
        setRepos(data.repos || []);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [isInView]);

  return (
    <section
      ref={ref}
      id="open-source"
      className="max-w-[1200px] mx-auto px-6 py-20 scroll-mt-24"
      data-testid="open-source-section"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p
            className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            Live from GitHub
          </p>
          <h2
            className={`text-3xl md:text-4xl text-[#051A24] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.1s' }}
          >
            Open{' '}
            <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
              source
            </span>
          </h2>
        </div>
        <a
          href="https://github.com/brandononchain"
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 text-sm font-medium text-[#051A24] hover:gap-3 transition-all py-3 min-h-[44px] ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
          style={{ animationDelay: '0.2s' }}
          data-testid="open-source-github-link"
        >
          <Github className="w-4 h-4" /> View all on GitHub <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>

      {status === 'loading' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="open-source-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <RepoSkeleton key={i} />
          ))}
        </div>
      )}

      {status === 'error' && (
        <div
          className="text-center py-16 border border-dashed border-[#E0EBF0] rounded-2xl"
          data-testid="open-source-error"
        >
          <p className="text-[#273C46] mb-4">Couldn't load repositories right now.</p>
          <a
            href="https://github.com/brandononchain"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#051A24] hover:gap-3 transition-all"
          >
            <Github className="w-4 h-4" /> Browse on GitHub <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {status === 'ready' && repos.length === 0 && (
        <p className="text-[#273C46] text-center py-16" data-testid="open-source-empty">
          No public repositories yet. Check back soon.
        </p>
      )}

      {status === 'ready' && repos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {repos.map((repo, index) => (
            <RepoCard key={repo.id} repo={repo} index={index} isInView={isInView} />
          ))}
        </div>
      )}
    </section>
  );
};

export default SelectedWorkSection;
